import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { AllNavigationSetsQuery } from "services/datocms/generated";
import {
  CollectionsDocument,
  CollectionsQuery,
} from "services/graphql/generated";
import { nameToSlug } from "@pepdirect/v3/helpers/string";

/**
 * Extended navigation set type with useTags flag
 */
export type NavigationSetWithFlag =
  AllNavigationSetsQuery["allNavigationSets"][0] & {
    useTags: boolean;
  };

/**
 * Transforms Collections query data into AllNavigationSetsQuery structure
 */
function transformCollectionsToNavigationSets(
  collections: CollectionsQuery["collections"]
): NavigationSetWithFlag[] {
  return collections.map((collection) => {
    const slug = nameToSlug(collection.name);

    return {
      __typename: "NavigationSetRecord",
      id: slug,
      name: collection.name,
      availableTags: [],
      useTags: false,
      navigationHeader: {
        __typename: "NavigationRecord",
        navigationItem: [
          {
            __typename: "ProductCategoryRecord",
            title: collection.name,
            id: slug,
            slug: slug,
            categoryImage: null,
          },
        ],
      },
    };
  });
}

/**
 * Fetches navigation sets from Algolia (GraphQL Collections).
 * Returns an empty array if Algolia is unavailable or returns no results.
 * @param gqlClient - Apollo Client instance for Algolia queries
 * @returns Navigation sets in AllNavigationSetsQuery format
 */
export async function fetchNavigationSets(
  gqlClient: ApolloClient<NormalizedCacheObject>
): Promise<NavigationSetWithFlag[]> {
  try {
    const { data } = await gqlClient.query<CollectionsQuery>({
      query: CollectionsDocument,
    });

    if (!data.collections || data.collections.length === 0) {
      console.warn(
        "[fetchNavigationSets] No collections returned from Algolia"
      );
      return [];
    }

    return transformCollectionsToNavigationSets(data.collections);
  } catch (error) {
    console.error("[fetchNavigationSets] Algolia fetch failed:", error);
    return [];
  }
}
